import { useMemo, useState } from "react";
import useDataFetcher from "../../../knisot/hooks/useFetchData";
import { SELLER } from "../../../../../../modules/API/Seller/seller";
import { ITransportationCompany, ITransportationOrderDetail } from "../../../../../../modules/API/Seller/models/transportation";
import styled from "styled-components";
import SearchInput from "../../../../../../generic/inputs/inputSearch";
  import Table from "../../../procedures/Q8/Layouts/Table/Table";
import TransportationItem from "./TransportationItem";
import useDebounce from "../../Helpers/useDebounce";
import { IFormattedDataByCustomer } from "./types/customerDataItem";
import TransportationCustomerItem from "./TransportationCustomerItem";
import TransportationGrowerItem from "./TransportationGrowerItem";
import { RadioSlider } from "../../../../../../generic/ui/Toggles/RadioSlider";
import { IFormattedDataByGrower } from "./types/growerData";
import useSellerController from "../../Service/useSellerController";
import { ISellerReqData } from "../../../../../../modules/API/Seller/models/reqests";
import { handleNotifications } from "../../../../../../generic/utils/handleNotifications";
import usePrint from "../../../../../../generic/hooks/usePrint";
import { PDF_URLS } from "../../Helpers/printConfig";
import { PdfPreview } from "../../../../../../generic/ui/PdfPreview";
import PrintIcon from "../../../../../../generic/icons/IconLib/PrintIcon";
import CustomBlockLoader from "../../../knisot/generic/CustomBlockLoader";

interface ITransportationMainProps {
  date: string
}

 
const SliderItems = [
  {id: 'transportation', label: 'הובלה'},
  {id: 'customer', label: 'לקוח'},
  {id: 'grower', label: 'מגדל'} 
]

 const TransportationMain = ({ date }: ITransportationMainProps) => {
    const [search, setSearch] = useState('')
    const [typeOfData, setTypeOfData] = useState<string>(SliderItems[1].id)
    const {
      handleGetPdf,
      handleSharePdf,
      file,
      handleDownloadPdf,
      handleClearFile,
      handleOpenPdf
    } = usePrint()
    const debouncedSearch = useDebounce(search, 500)
    const {
        data: transportationData,
        isLoading: transportationLoading,
    } = useDataFetcher<ITransportationCompany[]>(
        SELLER.getTransportation, date
    )
    const {
      putData,
    } = useSellerController()
    const handleChangeTypeOfData = (type: string) => {
      setTypeOfData(type)
    }
    const formattedData = useMemo(() => {
        if(!transportationData) return []
        if(typeOfData === 'transportation') {
          return transportationData
        }
        
        // flatten the data to one level by product object
        const flattenData = transportationData.flatMap(item => 
          item.Growers.flatMap(grower => grower.OrderDetails)
        )
        
        // Universal grouping function to prevent duplicates
        const groupData = <T extends 'customer' | 'grower'>(
          type: T,
          data: ITransportationOrderDetail[]
        ): T extends 'customer' ? IFormattedDataByCustomer[] : IFormattedDataByGrower[] => {
          const groupedMap = new Map()
          
          data.forEach(item => {
            const groupKey = type === 'customer' ? item.CustomerID : item.GrowerID
            const groupName = type === 'customer' ? item.Customer_Name : item.GrowerName
            
            if (!groupedMap.has(groupKey)) {
              groupedMap.set(groupKey, {
                [type === 'customer' ? 'CustomerID' : 'GrowerID']: groupKey,
                [type === 'customer' ? 'Customer_Name' : 'Grower_Name']: groupName,
                OrderDetails: []
              })
            }
            
            const group = groupedMap.get(groupKey)
            
            // Check if this product combination already exists
            const existingProduct = group.OrderDetails.find((order: any) => 
              order.product.ProductID === item.ProductID &&
              order.product.VarietyID === item.VarietyID &&
              order.product.SizeID.trim() === item.SizeID.trim()
            )
            
            if (!existingProduct) {
              // Find all related items for this product
              const relatedItems = data.filter(details =>
                details.ProductID === item.ProductID &&
                details.VarietyID === item.VarietyID &&
                details.SizeID.trim() === item.SizeID.trim() &&
                (type === 'customer' ? details.CustomerID === groupKey : details.GrowerID === groupKey)
              )
              
              group.OrderDetails.push({
                product: item,
                [type === 'customer' ? 'byGrower' : 'byCustomer']: relatedItems
              })
            }
          })
          
          return Array.from(groupedMap.values()) as any
        }
        
        if(typeOfData === 'customer') {
          return groupData('customer', flattenData)
        }
        else if(typeOfData === 'grower') {
          return groupData('grower', flattenData)
        }
        
        return []
    }, [transportationData, typeOfData])

    const renderTransportation = useMemo(() => {
        if(!transportationData) return []
        if(typeOfData === 'transportation') {
          return transportationData.filter(item => 
              item.FC_Name.trim().toLowerCase().includes(debouncedSearch.toLowerCase()) ||
              item.Growers.some(grower => grower.GrowerName.trim().toLowerCase().includes(debouncedSearch.toLowerCase())) ||
              item.Growers.some(grower => grower.OrderDetails.some(order => `${order.ProductDesc.trim()} ${order.VarietyDesc.trim()} ${order.SizeID.trim()}`.trim().toLowerCase().includes(debouncedSearch.toLowerCase())))
          )
        }
        return []
      }, [transportationData, debouncedSearch, typeOfData])

      const renderCustomer = useMemo(() => {
        if(!formattedData) return []
        else if(typeOfData === 'customer') {
          const result = [...formattedData] as IFormattedDataByCustomer[]
          return result.filter(item => item.Customer_Name.trim().toLowerCase().includes(debouncedSearch.toLowerCase()))
        }
        return []
      }, [formattedData, debouncedSearch, typeOfData])

      const renderGrower = useMemo(() => {
        if(!formattedData) return []
        else if(typeOfData === 'grower') {
          const result = [...formattedData] as IFormattedDataByGrower[]
          return result.filter(item => item.Grower_Name.trim().toLowerCase().includes(debouncedSearch.toLowerCase()))
        }
        return []
      }, [formattedData, debouncedSearch, typeOfData])

      const handleChangePrice = async (price: number[], item: ITransportationOrderDetail) => {
        const formedData: ISellerReqData = {
          RecID: item.RecID,
          ExpectedPriceFrom: price[0],
          ExpectedPriceTo: price[1],
          LineType: 2,
          CustomerID: item.CustomerID,
          GrowerID: item.GrowerID,
          Remarks: item.Remarks,
          Order_Pallets: item.Order_Pallets,
          ProductID: item.ProductID,
          VarietyID: item.VarietyID,
          SizeID: item.SizeID,
          Delivery_Date: item.Delivery_Date,
          FreightCompanyID: item.FreightCompanyID
        }
        try {
          await putData(formedData)
        } catch (error) {
          console.log(error)
          handleNotifications("שגיאה בעדכון מחיר", {type: "error"})
        }
      }

      const handlePrintByTransportation = async (FreightCompanyID: number) => {
        const dataUrl = PDF_URLS.byTransportation
        await handleGetPdf(`${dataUrl}?Date=${date}&FreightCompanyID=${FreightCompanyID}`)
      }

      const handlePrintByCustomer = async (OrderID: number) => {
        const dataUrl = PDF_URLS.byCustomer
        await handleGetPdf(`${dataUrl}?OrderID=${OrderID}`)
      }

      const handlePrintByGrower = async (GrowerID: number) => {
        const dataUrl = PDF_URLS.byGrower
        await handleGetPdf(`${dataUrl}?Date=${date}&GrowerID=${GrowerID}`)
      }

      const handlePrintGeneralClient = async () => {
        const dataUrl = PDF_URLS.byGeneralClient
        await handleGetPdf(`${dataUrl}?Date=${date}`)
      }
  
  return (
    <MainWrapper>
      {file && (
        <PdfViewerWrapper>
          <PdfPreview
            file={file}
            handleSharePdf={handleSharePdf}
            handleDownloadPdf={handleDownloadPdf}
            handleClearFile={handleClearFile}
            handleOpenPdf={handleOpenPdf}
          />
        </PdfViewerWrapper>
      )}
        <HeaderWrapper>
            <SearchInput 
            input={search}
            setInput={setSearch}
            />
            <RadioSlider
                items={SliderItems.map((item, index) => {
                  if(index === 1) {
                    return {
                      ...item,
                      icon: {
                        node: <PrintIcon />,
                        onClick: handlePrintGeneralClient
                      }
                    }
                  }
                  return item
                })}
                value={typeOfData}
                onChange={handleChangeTypeOfData}
            />
        </HeaderWrapper>
        <BodyWrapper>
            {
                transportationLoading ? (
                  <CustomBlockLoader />
                ) : (
                  typeOfData === 'transportation' ? (
                    renderTransportation.map((item) => (
                        <TransportationItem
                            key={item.FC_ID}
                            company={item}
                            handlePrintByTransportation={handlePrintByTransportation}
                        />
                    ))
                  ) : typeOfData === 'customer' ? (
                    renderCustomer.map((item) => (
                        <TransportationCustomerItem
                            key={item.CustomerID}
                            customer={item}
                            handleChangePrice={handleChangePrice}
                            handlePrintByCustomer={handlePrintByCustomer}
                        />
                    ))
                  ) : (
                    renderGrower.map((item) => (
                        <TransportationGrowerItem
                            key={item.GrowerID}
                            grower={item}
                            handlePrintByGrower={handlePrintByGrower}
                        />
                    ))
                  )
                )
            }
        </BodyWrapper>
    </MainWrapper>
  )
}
const MainWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5em;
  overflow: hidden;
  direction: rtl;
`
const PdfViewerWrapper = styled.div`
  position: absolute;
  inset: 0;
  padding: 0.5em;
  z-index: 10;
  background: ${({ theme }) => theme.colors.surface};
`
const HeaderWrapper = styled.div`
  width: 90%;
  display: flex;
  flex-flow: column;
  gap: 0.5em;
  align-items: center;
`
const BodyWrapper = styled.div`
  width: 95%;
  display: flex;
  flex-direction: column;
  /* gap: 0.2em; */
  overflow-y: auto;
  height: 90%;
  padding: 0.5em;
  &::-webkit-scrollbar {
    width: 0;
  }
`
export default TransportationMain;